import { useEffect, useState, useCallback } from "react";
import { Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { Shield, AlertTriangle, Car, Construction, Skull, Cone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import useChauffeurSocket from "@/hooks/useChauffeurSocket";

// Color + emoji per hazard type — keeps the SVG icon definition tiny and
// purpose-built so each marker is recognizable at a glance.
export const HAZARD_META = {
  police:      { color: "#3b82f6", emoji: "👮", label: "Police" },
  accident:    { color: "#ef4444", emoji: "🚨", label: "Accident" },
  traffic:     { color: "#f59e0b", emoji: "🚦", label: "Traffic" },
  danger:      { color: "#dc2626", emoji: "⚠",  label: "Danger" },
  road_closed: { color: "#a855f7", emoji: "🚧", label: "Road closed" },
  hazard:      { color: "#facc15", emoji: "⚡", label: "Hazard" },
};

export const HAZARD_ICONS = {
  police: Shield, accident: AlertTriangle, traffic: Cone, danger: Skull,
  road_closed: Construction, hazard: AlertTriangle,
};

function makeIcon(type) {
  const meta = HAZARD_META[type] || HAZARD_META.hazard;
  return new L.DivIcon({
    className: "hazard-pin",
    html: `<div style="width:30px;height:30px;border-radius:50%;background:${meta.color};border:3px solid #0a0a0a;box-shadow:0 0 14px ${meta.color}88;display:flex;align-items:center;justify-content:center;font-size:14px">${meta.emoji}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

function timeLeft(expiresAt) {
  try {
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const m = Math.round(ms / 60000);
    if (m < 60) return `${m}m left`;
    return `${Math.round(m / 60)}h left`;
  } catch { return ""; }
}

/**
 * HazardLayer — fetches active hazards near (centerLat,centerLng) and
 * renders them as react-leaflet Markers inside a parent <MapContainer>.
 * Live-updates via Socket.IO `hazard_added` / `hazard_confirmed` /
 * `hazard_cleared` so pins appear & disappear in real time.
 *
 * Mount as a child of <MapView>:
 *   <MapView ...><HazardLayer center={pickup}/></MapView>
 */
export default function HazardLayer({ center, radiusKm = 25, allowConfirm = true }) {
  const [hazards, setHazards] = useState([]);
  const { on } = useChauffeurSocket({ rideId: null });

  const refresh = useCallback(async () => {
    if (!center?.lat || !center?.lng) {
      setHazards([]);
      return;
    }
    try {
      const res = await api.get("/chauffeur/hazards", {
        params: { lat: center.lat, lng: center.lng, radius_km: radiusKm },
      });
      setHazards(res.data?.items || []);
    } catch {
      setHazards([]);
    }
  }, [center?.lat, center?.lng, radiusKm]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const offAdd = on("hazard_added", (h) => {
      setHazards((prev) => {
        if (prev.find((p) => p.id === h.id)) return prev;
        return [h, ...prev];
      });
    });
    const offConfirm = on("hazard_confirmed", (h) => {
      setHazards((prev) => prev.map((p) => (p.id === h.id ? { ...p, ...h } : p)));
    });
    const offClear = on("hazard_cleared", (payload) => {
      setHazards((prev) => prev.filter((p) => p.id !== payload.id));
    });
    return () => { offAdd(); offConfirm(); offClear(); };
  }, [on]);

  // Auto-prune expired pins client-side every 30s so the UI stays clean
  // even before MongoDB's TTL pruner fires.
  useEffect(() => {
    const t = setInterval(() => {
      setHazards((prev) => prev.filter((h) => {
        try { return new Date(h.expires_at).getTime() > Date.now(); }
        catch { return true; }
      }));
    }, 30000);
    return () => clearInterval(t);
  }, []);

  const confirm = async (id) => {
    try {
      await api.post(`/chauffeur/hazards/${id}/confirm`);
      toast.success("Confirmed");
    } catch { toast.error("Already confirmed"); }
  };
  const clear = async (id) => {
    try {
      await api.post(`/chauffeur/hazards/${id}/clear`);
      toast.success("Marked as cleared");
    } catch { toast.error("Could not clear"); }
  };

  return (
    <>
      {hazards.map((h) => {
        const meta = HAZARD_META[h.type] || HAZARD_META.hazard;
        return (
          <Marker
            key={h.id}
            position={[h.lat, h.lng]}
            icon={makeIcon(h.type)}
          >
            <Popup>
              <div data-testid={`hazard-popup-${h.id}`} style={{ minWidth: 160 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, color: meta.color }}>
                  <span>{meta.emoji}</span> {meta.label}
                </div>
                {h.note && <div style={{ fontSize: 11, marginTop: 4 }}>{h.note}</div>}
                <div style={{ fontSize: 10, color: "#666", marginTop: 4 }}>
                  {h.confirm_count > 0 && `✓ ${h.confirm_count} confirmed · `}{timeLeft(h.expires_at)}
                </div>
                {allowConfirm && (
                  <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                    <button data-testid={`hazard-confirm-${h.id}`}
                      onClick={() => confirm(h.id)}
                      style={{ flex: 1, fontSize: 10, padding: "3px 6px", background: "#10b981", color: "#fff", border: 0, borderRadius: 3, cursor: "pointer" }}
                    >Still there</button>
                    <button data-testid={`hazard-clear-${h.id}`}
                      onClick={() => clear(h.id)}
                      style={{ flex: 1, fontSize: 10, padding: "3px 6px", background: "transparent", color: "#888", border: "1px solid #ccc", borderRadius: 3, cursor: "pointer" }}
                    >Gone</button>
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}
